import { type FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { theme } from "shared/lib/styles";

import { AdSlot } from "scmp-app/components/advertisement/ad-slots/ad-slot";
import { TargetingPaidType } from "scmp-app/components/advertisement/ad-slots/types";
import { Ads1, Ads2, Ads3, Ads4, PremiumNativeAdContainer } from "scmp-app/pages/topic/styles";
import type { topicAdsTopic$key } from "scmp-app/queries/__generated__/topicAdsTopic.graphql";

import { getTopicAdsConfigs } from "./ads.config";

const adsComponentMap = [Ads2, Ads3, Ads4];

export type Props = {
  enablePremiumNativeAd?: boolean;
  hasSponsor: boolean;
  isShowSpecificWidget: boolean;
  maxAdsNumber: number;
  reference: topicAdsTopic$key;
};
export const TopicAds: FunctionComponent<Props> = ({
  enablePremiumNativeAd,
  hasSponsor,
  isShowSpecificWidget,
  maxAdsNumber,
  reference: reference_,
}) => {
  const data = useFragment(
    graphql`
      fragment topicAdsTopic on Topic {
        entityId
        advertZone(version: 2)
      }
    `,
    reference_,
  );

  const adsConfigs = getTopicAdsConfigs(
    data?.entityId ?? undefined,
    data?.advertZone ?? undefined,
    isShowSpecificWidget,
  ).slice(0, maxAdsNumber);

  const firstAdsConfig = adsConfigs[0];
  const restAdsConfigs = adsConfigs.slice(1);

  const renderFirstAds = () => {
    const FirstAds = firstAdsConfig.map(
      (config, configIndex) => config && <AdSlot {...config} key={configIndex} />,
    );

    return (
      <Ads1 $hasSponsor={hasSponsor}>
        {FirstAds}

        {!hasSponsor && enablePremiumNativeAd && (
          <PremiumNativeAdContainer>
            <AdSlot
              adUnit="d_native2f"
              breakpoint={theme.breakpoints.up("desktop")}
              sizes={[[1, 1], "fluid"]}
              targeting={{
                paid: TargetingPaidType.NotArticle,
                sctid: data.entityId,
              }}
              zone={data.advertZone ?? "default"}
            />
            <AdSlot
              adUnit="m_native2f"
              breakpoint={theme.breakpoints.down("desktop")}
              sizes={[[1, 1], "fluid"]}
              targeting={{
                paid: TargetingPaidType.NotArticle,
                sctid: data.entityId,
              }}
              zone={data.advertZone ?? "default"}
            />
          </PremiumNativeAdContainer>
        )}
      </Ads1>
    );
  };

  return (
    <>
      {renderFirstAds()}
      {restAdsConfigs.map((configs, index) => {
        const ComponentType = adsComponentMap[index];

        return (
          <ComponentType key={`AdSlot${index + 1}`}>
            {configs.map(
              (config, configIndex) => config && <AdSlot {...config} key={configIndex} />,
            )}
          </ComponentType>
        );
      })}
    </>
  );
};

TopicAds.displayName = "TopicAds";
